// 通知消息类型定义
export interface NotifyMsg {
  id: string
  title: string
  content?: string
  messageType: number // 10-通知，20-公告
  priority: number // 1-紧急，2-重要，3-普通，4-次要
  isExternal: boolean // 是否外部
  isPublic: boolean // 是否公开
  isSystem: boolean // 是否系统
  publisherUserName: string
  publisherNickName: string
  publishTime?: string
  status: number // 0-草稿，1-已发布，2-已撤回
  pushPlatforms?: string // 推送平台列表
  attachments?: NotifyMsgAttachment[] // 附件列表
  // 接收人信息
  receiverScope?: string // 接收范围
  selectedCustomers?: string[] // 选中的客户ID列表
  selectedStores?: string[] // 选中的门店ID列表
  selectedMembers?: string[] // 选中的成员ID列表
  selectedDepartments?: string[] // 选中的部门ID列表
  selectedUsers?: string[] // 选中的用户ID列表
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

export interface NotifyMsgDetail {
  id?: string
  title: string
  content?: string
  messageType: number // 10-通知，20-公告
  priority: number // 1-紧急，2-重要，3-普通，4-次要
  isExternal: boolean // 是否外部
  isPublic: boolean // 是否公开
  isSystem: boolean // 是否系统
  publisherUserName: string
  publisherNickName: string
  publishTime?: string
  status: number // 0-草稿，1-已发布，2-已撤回
  pushPlatforms?: string // 推送平台列表
  attachments?: NotifyMsgAttachment[] // 附件列表
  // 接收人信息
  receiverScope?: string // 接收范围
  receiverCustomers?: LabelValuePair[] // 选中的客户ID列表
  receiverStores?: LabelValuePair[] // 选中的门店ID列表
  selectedMembers?: LabelValuePair[] // 选中的成员ID列表
  receiverDepts?: LabelValuePair[] // 选中的部门ID列表
  receiverUsers?: LabelValuePair[] // 选中的用户ID列表
  platformExts?: NotifyPlatformExt[] // 平台扩展配置
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

export interface LabelValuePair{
  label: string
  value: string
}

// 第三方平台消息扩展
export interface NotifyPlatformExt {
  id?: string
  notifyMsgId: string
  platformType: string // WECHAT_WORK-企业微信，wechat_mp-微信公众号
  msgType: string // text-文本，image-图片，voice-语音，video-视频，news-图文，miniprogram-小程序，mpnews-公众号图文，wxcard-卡券
  content?: string
  mediaId?: string
  title?: string
  description?: string
  url?: string
  picurl?: string
  platformSpecific?: Record<string, any>
  platformMsgId?: string
  isRead?: boolean
  readTime?: string
  isClicked?: boolean
  clickTime?: string
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

// 推送状态
export interface NotifyPushStatus {
  id?: string
  notifyMsgId: string
  platformType: string
  pushStatus: number // 0-未推送，1-推送中，2-推送成功，3-推送失败
  pushTime?: string
  failReason?: string
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

// 用户平台关联
export interface NotifyUserPlatform {
  id?: string
  userId: string
  platformType: string
  platformUserId: string
  platformUnionid?: string
  platformNickname?: string
  isSubscribed?: boolean
  subscribeTime?: string
  isActive?: boolean
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

// 通知接收用户
export interface NotifyUser {
  id?: string
  notifyMsgId: string
  userId: string
  readStatus: number // 0-未读，1-已读
  readTime?: string
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

// 用户未读消息（包含消息详情）
export interface UserUnreadNotify {
  id: string // 用户消息ID
  notifyMsgId: string // 通知消息ID
  title: string
  content?: string
  messageType: number
  messageTypeName: string
  priority: number
  priorityName: string
  isExternal: boolean
  publisherUserName: string
  publisherNickName?: string
  publishTime: number // 时间戳
  pushPlatforms?: string
  readStatus: boolean
  createTime: number // 时间戳
}

// 通知附件
export interface NotifyMsgAttachment {
  id?: string
  notifyMsgId: string
  fileName: string
  filePath: string
  fileSize: number
  fileType?: string
  url?: string // 文件访问URL
  downloadCount?: number
  uploadTime?: string
  creator?: string
  updater?: string
  createTime?: string
  updateTime?: string
  deleted?: boolean
  version?: number
}

// 查询参数
export interface NotifySearchParams {
  keyword?: string
  messageType?: number
  priority?: number
  status?: number
  isExternal?: boolean
  platformType?: string
  startTime?: string
  endTime?: string
  current?: number
  pageNo?: number
  pageSize?: number
}

// 创建通知参数
export interface CreateNotifyParams {
  title: string
  content?: string
  messageType: number
  priority: number
  isExternal: boolean
  isPublic: boolean
  isSystem: boolean
  pushPlatforms?: string[]
  platformExts?: NotifyPlatformExt[]
  attachments?: File[]
  // 接收人信息
  receiverScope?: string // 接收范围
  selectedCustomers?: string[] // 选中的客户ID列表
  selectedStores?: string[] // 选中的门店ID列表
  selectedMembers?: string[] // 选中的成员ID列表
  selectedDepartments?: string[] // 选中的部门ID列表
  selectedUsers?: string[] // 选中的用户ID列表
}

// 更新通知参数
export interface UpdateNotifyParams extends CreateNotifyParams {
  id: string
}

// 分页响应
export interface NotifyPageResponse {
  list: NotifyMsg[]
  total: number
  current: number
  pageSize: number
}

// 常量定义
export const MESSAGE_TYPES = {
  NOTIFY: 10,
  ANNOUNCEMENT: 20,
} as const

export const PRIORITY_LEVELS = {
  URGENT: 1,
  IMPORTANT: 2,
  NORMAL: 3,
  MINOR: 4,
} as const

export const NOTIFY_STATUS = {
  DRAFT: 0,
  PUBLISHED: 1,
  WITHDRAWN: 2,
} as const

export const PUSH_STATUS = {
  NOT_PUSHED: 0,
  PUSHING: 1,
  SUCCESS: 2,
  FAILED: 3,
} as const

export const PLATFORM_TYPES = {
  WECHAT_WORK: 'WECHAT_WORK',
  WECHAT_MP: 'wechat_mp',
} as const

export const MSG_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  VOICE: 'voice',
  VIDEO: 'video',
  NEWS: 'news',
  MINIPROGRAM: 'miniprogram',
  MPNEWS: 'mpnews',
  WXCARD: 'wxcard',
} as const

// 操作日志相关类型定义
export interface NotifyOperationLog {
  id?: string
  notifyMsgId: string
  operationType: number
  operationTypeName: string
  operatorId: string
  operatorUsername: string
  operatorNickname: string
  operationDesc: string
  beforeStatus: number
  afterStatus: number
  beforeStatusName: string
  afterStatusName: string
  operationTime: string
}

// 操作日志分页查询参数
export interface NotifyOperationLogQueryParams {
  notifyMsgId: string
  current?: number
  pageSize?: number
}

// 操作日志分页响应
export interface NotifyOperationLogPageResponse {
  list: NotifyOperationLog[]
  total: number
  current: number
  pageSize: number
}

// 操作类型常量
export const OPERATION_TYPES = {
  CREATE: 10,
  UPDATE: 20,
  PUBLISH: 30,
  WITHDRAW: 40,
  DELETE: 50,
  PUSH: 60,
} as const

